/* eslint-disable react/no-unescaped-entities */
import styled from 'styled-components';
import { useRouter } from 'next/router';
import Image from 'next/image';
import Link from 'next/link';

import ButtonLink from '../../components/atoms/button-link/button-link';
import LoadingIcon from '../../components/atoms/loading-icon/loading-icon';
import TabTitle from '../../components/atoms/tab-title/tab-title';
import { MetaPages } from '../../enums/pages';
import SocialMediaShareSection from '../../components/molecules/social-media-share-section/social-media-share-section';
import { getFeaturedFriendsOfTheKitchenPageDetails } from '../../services/inspirations-and-planning';
import { FriendsOfTheKitchenPageDetailsType } from '../../types/content';
import Breadcrumbs from '../../components/atoms/breadcrumbs/breadcrumbs';
import ContainerWithSidebar from '../../components/atoms/container-with-sidebar/container-with-sidebar';
import MarginSpacing from '../../components/atoms/margin-spacing/margin-spacing';
import FeaturesSidebar from '../../components/molecules/features-sidebar/features-sidebar';
import { pageRoute } from '../../config/routes/pages';
import { NewsAndEventsModel } from '../../interface/news-and-events';
import { getFeaturedNewsAndEvents } from '../../services/news';
import Carousel from '../../components/molecules/carousel/carousel';
import { linkRoutes } from '../../config/routes/links';
import { strapiAssetBaseUrl } from '../../config/routes/assets';

const Container = styled.div`
  p {
    font-family: MuseoSans-100;
    font-size: 14px;
    font-weight: 200 !important;
    margin: 20px 0;
    text-align: justify;
  }

  em {
    font-size: 12px;
  }

  p strong {
    font-family: MuseoSans-500;
  }

  ul {
    list-style-type: circle;
    padding-left: 40px;
  }

  h1 {
    font-family: ChronicleDisplayLight;
    font-size: 35px;
    font-weight: 300 !important;
    text-transform: uppercase;
    line-height: 2.25rem;
  }

  a {
    text-decoration-line: underline;
  }

  a:hover {
    color: #0081c6;
  }
`;

type IndexProps = {
  featuredFriendsOfTheKitchenPageDetails: FriendsOfTheKitchenPageDetailsType[];
  featuredNewsAndEvents: NewsAndEventsModel[];
  newsAndEventsArticle: NewsAndEventsModel;
};

const links = {
  showroom: 'https://my.matterport.com/show/?m=Boe2AFEuzJe',
  dualRefSystemArticle: 'https://subzero-wolf.com.ph/news-and-events/26659',
  inductionArticle: 'https://subzero-wolf.com.ph/news-and-events/26657',
  bookAnAppointment:
    'https://calendly.com/fgi-kitchen-bookings?utm_source=Instagram&utm_medium=Bio_Link&utm_campaign=Calendly_Appointments&utm_content=SZWF'
};

const carouselImages = [
  `${strapiAssetBaseUrl}/1_2bc96ffe58.jpg`,
  `${strapiAssetBaseUrl}/2_b536900e8d.jpg`,
  `${strapiAssetBaseUrl}/3_7b85c59c46.jpg`,
  `${strapiAssetBaseUrl}/4_e6def47178.jpg`
];

function ArticleContent() {
  return (
    <article className="flex flex-col gap-5">
      <div>
        <h1>REDISCOVERING FILIPINO CLASSICS</h1>
        <p>
          Delight in a symphony of soulful flavors that pay homage to classic
          Filipino recipes reimagined with a contemporary touch. Join our feast
          as we transform each dish into a celebration with the outstanding
          performance of Sub-Zero and Wolf appliances.
        </p>
        <p>
          April 27, 2024 - Food serves as the core in every Filipino gathering.
          Be it a festive occasion or a casual get-together, food invariably
          takes center stage, fostering connections among us. Sub-Zero and Wolf
          conducted a cooking demo in the brands’{' '}
          <Link href={links.showroom}>exclusive showroom</Link> at The
          Residences at Greenbelt in Makati City. Focus Global's corporate chefs
          excited guests by elevating dishes inspired by classic Filipino
          recipes.
        </p>
      </div>
      <Carousel imageURLs={carouselImages} name="Exclusive Showroom" />

      <div className="flex flex-col gap-8 my-5">
        <div className="flex w-full flex-col md:flex-row justify-between items-stretch gap-4">
          <div className="relative w-full h-[300px] sm:h-96 md:h-auto md:w-1/2">
            <Image
              layout="fill"
              sizes="100vw"
              alt="Baby Squid Adobo on Toasted Pandesal"
              className="w-full h-full object-cover md:flex-1"
              src={`${strapiAssetBaseUrl}/5_97104fa054.png`}
            />
          </div>
          <p className="w-full text-justify md:w-1/2">
            The afternoon started with Baby Squid Adobo on Toasted Pandesal, a
            Filipino staple transformed into an easy-to-eat appetizer. The
            savory, tangy, and slightly sweet dish was created using the{' '}
            <Link href="https://subzero-wolf.com.ph/news-and-events/26657">
              Wolf Induction Cooktop
            </Link>{' '}
            which boasts of its instantaneous heat transfer, providing the most
            efficient method to cook. The precise temperature of Wolf is perfect
            for cooking delicate ingredients like squid, ensuring they are
            tender and flavorful every time. The pandesal was toasted in the{' '}
            <Link href="https://subzero-wolf.com.ph/news-and-events/26727">
              Wolf M Series Oven
            </Link>
            , showcasing its large cavity which is perfect for preparing for a
            big gathering. Using Convection Mode, the even heat ensured that
            each pandesal had a golden exterior while maintaining its soft
            texture inside.
          </p>
        </div>

        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="relative w-full h-[300px] sm:h-96 md:h-[300px] md:w-1/2">
            <Image
              layout="fill"
              sizes="100vw"
              alt="Chicken Inasal Skewers"
              className="object-cover flex-1 md:order-last"
              src={`${strapiAssetBaseUrl}/6_f0bc3754b4.jpg`}
            />
          </div>
          <p className="w-full text-justify justify-center md:order-first md:w-1/2">
            The next appetizer demonstrated was the Chicken Inasal Skewers
            presented as bite-sized pieces topped with a special aioli made with
            chicken oil. Using the Wolf Induction Cooktop, the chefs were able
            to achieve the smokey and charred exterior of the inasal even
            without an actual grill. The Power Boost Setting offered the high
            heat required to create dishes like this. Wolf’s precision cooking
            showcased its capability to elevate a traditional favorite at home.
          </p>
        </div>
      </div>

      <div className="flex flex-col">
        <div className="flex flex-col md:flex-row gap-4">
          <Image
            alt="Beef Tenderloin Morcon with Longganisa and Kesong
        Puti Stuffing"
            className="object-cover"
            height={400}
            src={`${strapiAssetBaseUrl}/7_a81b74fb5f.jpg`}
            width={500}
          />
          <Image
            alt="Wolf M Series Oven"
            className="object-cover"
            height={400}
            src={`${strapiAssetBaseUrl}/8_2af7298a9b.jpg`}
            width={500}
          />
        </div>
        <p className="mt-[0.9375rem]">
          For the main dish, the Beef Tenderloin Morcon with Longganisa and
          Kesong Puti Stuffing was inspired by the traditional Morcon created
          from thinly sliced beef rolled and stuffed with sausage or hotdogs,
          carrots, pickles, cheese, and egg. The morcon was brought to life
          using the Convection Roast mode on the Wolf M Series Oven. This
          allowed for deeper browning on the beef’s exterior with the filling
          cooked to perfection. The freshness and quality of the ingredients
          enhanced the overall flavor of the dish. Every bite of the Beef
          Tenderloin Morcon was filled with delicious flavors, blending the
          taste of sweet and savory.
        </p>
      </div>

      <div className="flex flex-col">
        <div className="flex flex-col md:flex-row gap-4">
          <Image
            alt="Mahi-Mahi and Bauko Beans Confit"
            className="object-cover"
            height={400}
            src={`${strapiAssetBaseUrl}/9_0fa25fb864.png`}
            width={500}
          />
          <Image
            alt="Wolf M Series Oven"
            className="object-cover"
            height={400}
            src={`${strapiAssetBaseUrl}/10_2c01389cea.png`}
            width={500}
          />
        </div>
        <p>
          A Filipino meal is not complete without dessert and what better way to
          savor this by reintroducing a traditional treat, Puto Bumbong. This is
          typically served with butter, grated coconut, and muscovado sugar. For
          an elevated twist, it was topped with keso de bola and a special
          muscovado pastry cream that added another layer of flavor to the local
          favorite. Traditionally, cooking puto bumbong involves the tedious
          process of steaming ground glutinous rice batter in bamboo tubes
          arranged vertically in a pot. This method has to be done repeatedly as
          the conventional steamer only fits 3-4 tubes at a time. With the{' '}
          <Link href="https://subzero-wolf.com.ph/news-and-events/24507">
            Wolf Convection Steam Oven
          </Link>
          , the process was simplified as the chefs were able to easily make a
          large batch by placing the rice batter into foil molds and directly
          cooking them in the CSO at once, achieving the same delicious results.
          This cozy dessert was paired with Tsokolate de Batirol Coffee
          combining a classic Filipino hot chocolate drink with well-loved
          espresso created in our Wolf Coffee System.
        </p>
      </div>

      <div>
        <Image
          alt="Cooking Demo with Audience"
          height={600}
          src={`${strapiAssetBaseUrl}/11_ee192b0962.jpg`}
          width={960}
        />
        <p>
          Experience rediscovered Filipino classics like no other with Sub-Zero
          and Wolf by{' '}
          <Link href={links.bookAnAppointment}>
            booking an appointment with us
          </Link>
          .
        </p>
      </div>
    </article>
  );
}

export function Index(props: IndexProps) {
  const {
    featuredFriendsOfTheKitchenPageDetails,
    featuredNewsAndEvents,
    newsAndEventsArticle
  } = props;

  const router = useRouter();

  if (router.isFallback) {
    return <LoadingIcon />;
  }

  const breadcrumbs = {
    route: newsAndEventsArticle.attributes.slug,
    tabLabel: newsAndEventsArticle.attributes.news_title
  };

  const newsAndEvents = {
    route: pageRoute.NEWS_AND_EVENTS,
    tabLabel: 'NEWS AND EVENTS'
  };

  return (
    <>
      <TabTitle page={MetaPages.NEWS_AND_EVENTS} />
      <ContainerWithSidebar
        featuredContents={
          <FeaturesSidebar
            friendsOfTheKitchenPageDetails={
              featuredFriendsOfTheKitchenPageDetails
            }
            newsAndEventsContent={featuredNewsAndEvents}
          />
        }
      >
        <Breadcrumbs
          color="text-breadcrumbGray"
          subTab={breadcrumbs}
          tab={newsAndEvents}
        />
        <Container>
          <div className="mt-0 sm:mt-10" />
          <ArticleContent />
          <div className="flex justify-end ml-auto mt-10">
            <ButtonLink
              buttonTitle="BACK"
              ctaLink={pageRoute.NEWS_AND_EVENTS}
            />
          </div>
          <MarginSpacing margin="mb-20" />
        </Container>
      </ContainerWithSidebar>
      <MarginSpacing margin="mb-5" />
      <SocialMediaShareSection />
    </>
  );
}

export async function getStaticProps() {
  const featuredFriendsOfTheKitchenPageDetails =
    await getFeaturedFriendsOfTheKitchenPageDetails();
  const featuredNewsAndEvents = await getFeaturedNewsAndEvents();
  const newsAndEventsArticle = {
    attributes: {
      slug: '042724',
      news_title: 'Rediscovering Filipino Classics'
    }
  } as NewsAndEventsModel;

  return {
    props: {
      featuredFriendsOfTheKitchenPageDetails,
      featuredNewsAndEvents,
      newsAndEventsArticle
    },
    revalidate: 300
  };
}

export default Index;
